import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  tooltipClasses,
  CircularProgress,
  Badge,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import ActionsDropdown from "../../../../core/common/crmComponents/ActionsDropdown";
import {
  MODULES,
  MODULES_ACTIONS,
} from "../../../../core/data/constants/moduleConstants";
import LeadApi from "../api/LeadApi";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import HasPermission from "../../../../core/common/wrapper/HasPermission";
import {
  CALLER,
  COMPANY_ADMIN,
  SUPER_ADMIN,
  TEAM_MANAGER,
} from "../../../../core/data/constants/constant";
import { useCallHandler } from "../../../../utils/hooks/useCallHandler";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import {
  convertSecondsToHMS,
  convertToUTCString,
} from "../../../../utils/helpers/helper";
import { useUsersByRole } from "../../manageUsers/hooks/useUserByRole";
import { setFilterModel } from "../../../../core/data/redux/filterSlice";
import { customMuiDateFilter } from "../../../../utils/helpers/customMuiDateFilter";
import { HiDuplicate } from "react-icons/hi";
import { formatEnrollmentData, formatLeadsData } from "../../../../utils/helpers/formatModulesData";
import SocialMediaIcons from "../../../../core/common/crmComponents/SocialMediaIcons";
import { FiClock, FiSettings } from "react-icons/fi";
import useReminders from "../../reminders/hooks/useReminders";
import { FaCheck, FaMinus } from "react-icons/fa";
import ZoomMeetingStatus from "../../../../core/common/crmComponents/zoomMeeting/ZoomMeetingStatus";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";

const LeadList = ({ leadsData, selectedCompany, setFilteredData }) => {
  const route = all_routes;
  const { user } = useSelector((state) => state?.auth);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
    const [isOpenPostCallModal, setIsOpenPostCallModal] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [shareData,setShareData]=useState({})
  const [hasMore, setHasMore] = useState(true);
  const [limit, setLimit] = useState(10);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const { mutate, isPending, isSuccess } = useCallHandler(
    MODULES.LEADS,
    selectedLeadId
  );
    const hasShareLead = useFunctionalityAccess("shareLeads");
      const openModal = () => setIsOpenPostCallModal(true)
  const closeModal = () => setIsOpenPostCallModal(false)

  const { reminders } = useReminders();

  const filterModel = useSelector((state) => state.filter.filterModel);
  const dispatch = useDispatch();

  const allParams = getAllUrlParams(); // Get all URL params
  const queryString = new URLSearchParams(allParams).toString();
  const canChangeLeadOwner = useFunctionalityAccess("changeLeadOwnerShip");
  const hasZoomAccess = useFunctionalityAccess("zoomSetting");
  const hasCallPermission = useFunctionalityAccess("CallPermission");

  // Function to check if a lead has a reminder
  const hasReminder = (leadId) => {
    return reminders?.some((reminder) => reminder.leadId === leadId);
  };

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useUsersByRole({
    role: "service-caller",
    page : 1,
    limit: 50,
    ofCompany: selectedCompany,
    status: "active",
    enable: isDialogOpen,
  });

  useEffect(() => {
    if (data?.data) {
      setUsers((prev) => {
        // Use a Map to remove duplicates based on _id
        const uniqueUsers = new Map(prev.map((user) => [user._id, user]));
        data.data.forEach((user) => uniqueUsers.set(user._id, user));

        return Array.from(uniqueUsers.values());
      });

      setHasMore(data?.data?.length >= limit); // Check if more data is available
    }
  }, [data]);

  const handleSelectionChange = (id, callerid) => {
    setSelectedLeadIds((prev) =>
      prev.some((el) => el.id === id)
        ? prev.filter((el) => el.id !== id)
        : [...prev, { id, callerid }]
    );
  };

  const openChangeOwnerDialog = () => {
    setIsDialogOpen(true);
    setPage(1);
    // setUsers([]);
  };

  const changeLeadOwner = async () => {
    try {
      const leadIds = selectedLeadIds
        .filter((el) => el.callerid !== selectedUserId)
        .map((el) => el.id);

      if (leadIds.length === 0) {
        toast.warning("Selected leads already belong to this caller.");
        setIsDialogOpen(false);
        setPage(1);
        setHasMore(true);
        setSelectedUserId(null);
        setSelectedLeadIds([]);
        return;
      }
      if (selectedLeadIds.length > leadIds.length) {
        toast.warning(
          "Some selected leads belong to the same caller and won't be updated."
        );
      }

      const res = await LeadApi.changleLeadOwner(leadIds, selectedUserId);
      toast.success(res.msg || "Leads reassigned successfully.");
      queryClient.invalidateQueries("lead");

      setIsDialogOpen(false);
    } catch (error) {
      toast.error(error.response.data?.msg || "Failed to change lead owner.");
    } finally {
      setPage(1);
      setHasMore(true);
      setSelectedUserId(null);
      setSelectedLeadIds([]);
    }
  };

  // Flatten the leadsData array
  const processedRows = formatEnrollmentData(leadsData);

  const columns = [
     ...(canChangeLeadOwner
          ? [
              {
                field: "id",
                headerName: "ID",
                width: 50,
                renderCell: (params) => (
                  <HasPermission
                    module={MODULES.USERS}
                    action={MODULES_ACTIONS.UPDATE}
                  >
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.some(
                        (el) => el.id === params.row.id
                      )}
                      onChange={() =>
                        handleSelectionChange(
                          params.row.id,
                          params.row.assignedToId
                        )
                      }
                    />
                  </HasPermission>
                ),
                renderHeader: () => (
                  <Tooltip title="ID" arrow>
                    <span>ID</span>
                  </Tooltip>
                ),
              },
            ]
          : []),
    {
      field: "enrolledOn",
      headerName: "Enrolled On",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.enrolledOn
            ? convertToUTCString(params?.row?.enrolledOn)
            : convertToUTCString(params?.row?.lastActivityDate)}
        </div>
      ),
    },
    
    {
      field: "fullName",
      headerName: "Client Name",
      width: 180,
      renderCell: (params) => {
        const leadDetailsUrl = `${route.enrollmentDetails.replace(
          ":id",
          params.row.id
        )}?${queryString}`;

        return (
          <div className="d-flex align-items-center">
         {/* {console.log("Rendering lead", params.row, "with name", params.row.clientFullName)} */}
            {hasReminder(params.row.id) && (
              <FiClock
                className="me-2 flex-shrink-0"
                size={18}
                style={{
                  color: "#ff9900",
                }}
              />
            )}
            <Link
              className="text-capitalize list-link text-truncate"
              to={leadDetailsUrl}
              style={{
                maxWidth: "250px", // Adjust width as needed
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.fullName || "N/A"}
            </Link>
          </div>
        );
      },
    },
    {
      field: "mobile",
      headerName: "Phone",
      width: 160,
      renderCell: (params) => (
        <div>
          {hasCallPermission && user?.agentId && user?.campaignName ? (
            <Tooltip
              title="Click To Call"
              placement="bottom"
              slotProps={{
                popper: {
                  sx: {
                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                      {
                        marginTop: "0px", // Adjust this to reduce space
                        transform: "translateY(0px)",
                      },
                  },
                },
              }}
            >
              <button
                className="d-flex align-items-center nav-link border-0 bg-transparent"
                onClick={() => {
                  if (!isPending) {
                    setSelectedLeadId(params.id);
                    mutate(params?.row?.mobile);
                    openModal();
                  }
                }}
                disabled={isPending}
              >
                <div className="p-0 flex-shrink-0 text-primary me-2 fs-5">
                  {isPending && selectedLeadId === params.id ? (
                    <i className="ti ti-loader spinner" />
                  ) : (
                    <i className="ti ti-phone" />
                  )}
                </div>
                <p className="mb-0">{params?.row?.mobile || "N/A"}</p>
              </button>
            </Tooltip>
          ) : hasCallPermission ? (
            <a
              onClick={(e) => {
    e.preventDefault();
    setSelectedLeadId(params.id);
    openModal();
    setTimeout(() => {
      window.location.href = `tel:${params?.row?.mobile}`;
    }, 500); // Delay to allow modal to open or animate
  }}
              className="d-flex align-items-center nav-link border-0 bg-transparent "
            >
              <div className="p-0 flex-shrink-0 text-primary me-2 fs-5" onClick={()=>{
                    openModal();
              }}>
                <i className="ti ti-phone" />
              </div>
              <p className="mb-0">{params?.row?.mobile || "N/A"}</p>
            </a>
          ) : (
            <p className="mb-0">{params?.row?.mobile || "N/A"}</p>
          )}
        </div>
      ),
    },
      {
      field: "email",
      headerName: "Email",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.email || "N/A"}
        </div>
      ),
    },
    {
      field: "course",
      headerName: "Course",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.course || "N/A"}
        </div>
      ),
    },
    {
      field: "medium",
      headerName: "Medium",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.medium || "N/A"}
        </div>
      ),
    },
    {
      field: "assignedToName",
      headerName: "Assigned To",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.assignedTo || "N/A"}
        </div>
      ),
    },
    

    {
      field: "informationCallDate",
      headerName: "Information Call Date",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.informationCallDate
            ? convertToUTCString(params?.row?.informationCallDate)
            :"N/A"}
        </div>
      ),
    },
    {
      field: "joiningProcessCallDate",
      headerName: "Joining Process Call Date",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.joiningProcessCallDate
            ? convertToUTCString(params?.row?.joiningProcessCallDate)
            :"N/A"}
        </div>
      ),
    },
    {
      field: "serviceFirstCallOn",
      headerName: "Service First Call On",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.serviceFirstCallOn
            ? convertToUTCString(params?.row?.serviceFirstCallOn)
            :"N/A"}
        </div>
      ),
    },
    {
      field: "serviceSecondCallOn",
      headerName: "Service Second Call On",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.serviceSecondCallOn
            ? convertToUTCString(params?.row?.serviceSecondCallOn)
            :"N/A"}
        </div>
      ),
    },
    {
      field: "serviceThirdCallOn",
      headerName: "Service Third Call On",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.serviceThirdCallOn
            ? convertToUTCString(params?.row?.serviceThirdCallOn)
            :"N/A"}
        </div>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.lastActivityDate
            ? convertToUTCString(params?.row?.lastActivityDate)
            : convertToUTCString(params?.row?.createdTime)}
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {selectedLeadIds.length > 0 && (
        <Box mb={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={openChangeOwnerDialog}
          >
            Change Enrollment Assigned Owner
          </Button>
        </Box>
      )}
      <DataGrid
        rows={processedRows}
        columns={columns.map((column) => ({
          ...column,
          headerClassName: "bg-light-400 ",
        }))}
        autoHeight
        hideFooter
        disableExtendRowFullWidth
        onStateChange={(state) => {
          const filteredRowIds = Object.entries(state.filter.filteredRowsLookup)
            .filter(([_, isVisible]) => isVisible === true)
            .map(([rowId]) => rowId); // Keep IDs as strings

          // Filter leadsData based on these IDs
          const filteredData = leadsData?.filter((row) =>
            filteredRowIds.includes(row?._id)
          );
          // Pass the filtered data to the parent
          // setFilteredData(filteredData);
        }}
        sx={{
          "& .sticky-column": {
            position: "sticky",
            right: 0,
            background: "white", // Prevents content from hiding under it
            zIndex: 100, // Keeps it on top
          },
          "& .MuiDataGrid-columnHeaders": {
            background: "white",
            position: "sticky",
            top: 0,
            zIndex: 110,
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
        }}
        // filterModel={filterModel}
        // onFilterModelChange={(newModel) => dispatch(setFilterModel(newModel))}
        // slots={{
        //   toolbar: GridToolbar, // Enable filter toolbar above the grid
        // }}
        // slotProps={{
        //   toolbar: {
        //     // showQuickFilter: true, // Show Quick Filter
        //     printOptions: { disableToolbarButton: true }, // Hide Print Button
        //     csvOptions: { disableToolbarButton: true }, // Hide CSV Export
        //   },
        // }}
      />
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select New Owner</DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={150}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <List className="text-capitalize">
                {users.map((user) => (
                  <ListItem
                    key={user._id}
                    className={`${
                      selectedUserId === user._id
                        ? "bg-primary text-white rounded-pill"
                        : ""
                    }`}
                    disablePadding
                    sx={{
                      mb: 1,
                      borderRadius: "8px",
                      "&.bg-primary": { color: "white" },
                    }}
                  >
                    <ListItemButton
                      onClick={() => setSelectedUserId(user._id)}
                      sx={{ paddingY: "6px", paddingX: "12px" }} // Reduce padding
                    >
                      <ListItemText primary={user.name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {hasMore && (
                <Box mt={2} textAlign="center">
                  <Button
                    variant="outlined"
                    onClick={() => setPage((prev) => prev + 1)}
                    color="primary"
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setIsDialogOpen(false);
              setPage(1);
              setHasMore(true);
              setSelectedUserId(null);
              setSelectedLeadIds([]);
            }}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={changeLeadOwner}
            disabled={!selectedUserId}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
    </div>
  );
};

export default LeadList;
